import PropTypes from 'prop-types';
import React from 'react';

import {
  hasLoggedInUser,
  getCurrentUser,
  getCustomData,
} from '../utils/b2c-utils';
import { getDefaultClientCode } from './Common/whitelabel';
import {
  updateGroupById,
} from '../services/GroupsApi';
import {
  addUser as addUserApi,
  findUsersByQuery,
  updateUser as updateUserApi,
} from '../services/UserApi';
import {
  loginWithB2C,
} from '../services/AuthApi';

// Create a React Context that lets us expose and access auth state
// without passing props through many levels of the component tree
const StitchAuthContext = React.createContext();

// Create a React Hook that lets us get data from our auth context
export function useStitchAuth() {
  const context = React.useContext(StitchAuthContext);
  if (!context) {
    throw new Error(
      `useStitchAuth must be used within a StitchAuthProvider`,
    );
  }
  return context;
}

// Create a component that controls auth state and exposes it via
// the React Context we created.
export function StitchAuthProvider(props) {
  const [authState, setAuthState] = React.useState({
    isLoggedIn: hasLoggedInUser(),
    currentUser: getCurrentUser(),
    userCustomData: null,
    clientName: getDefaultClientCode(),
    otherUsers: [],
  });

  // We useMemo to improve performance by eliminating some re-renders
  const authInfo = React.useMemo(() => {
    // Authentication Actions
    const handleLogout = async () => {
      const { isLoggedIn } = authState;
      if (isLoggedIn) {
        // Clear B2C tokens
        localStorage.removeItem('b2c_access_token');
        localStorage.removeItem('b2c_id_token');
        localStorage.removeItem('b2c_refresh_token');
        localStorage.removeItem('b2c_token_expires_at');

        setAuthState({
          ...authState,
          isLoggedIn: false,
          currentUser: null,
          userCustomData: null,
          otherUsers: [],
        });
      }
    };


    const updateUser = async (user, data) => {
      try {
        // user.id is the b2cUserId, we need to find the MongoDB _id first
        const queryResult = await findUsersByQuery({ id: user.id });
        if (queryResult?.users?.length > 0) {
          const mongoUser = queryResult.users[0];
          const updateRes = await updateUserApi(mongoUser._id, data);
          return updateRes;
        }
        throw new Error('User not found');
      } catch (error) {
        console.error('Error updating user:', error);
        throw error;
      }
    };

    const handleAddUser = async (newUserData) => {
      try {
        const userData = {
          email: newUserData.email,
          name: newUserData.name,
          namePublic: newUserData.namePublic,
          title: newUserData.title || '',
          groupIds: newUserData.groupIds,
          role: newUserData.role || 'user',
          isSuperAdmin: newUserData.isSuperAdmin || false,
          superUserAccess: newUserData.superUserAccess || false,
          workspaceAccess: newUserData.workspaceAccess !== undefined ? newUserData.workspaceAccess : true,
          timeZone: newUserData.timeZone || authState.userCustomData?.timeZone || 'America/Los_Angeles',
        };

        const result = await addUserApi(userData);
        return result;
      } catch (err) {
        if (err.response?.status === 409) {
          throw new Error('User already exists');
        }
        // Re-throw the error to be handled by the calling component's catch block
        throw err;
      }
    };

    const handleB2CLogin = async (email, password) => {
      try {
        // Login with B2C
        const tokens = await loginWithB2C(email, password);

        // Store tokens in localStorage
        localStorage.setItem('b2c_access_token', tokens.accessToken);
        localStorage.setItem('b2c_id_token', tokens.idToken);
        if (tokens.refreshToken) {
          localStorage.setItem('b2c_refresh_token', tokens.refreshToken);
        }


        // Set token expiration
        const expiresAt = Date.now() + (tokens.expiresIn * 1000);
        localStorage.setItem('b2c_token_expires_at', expiresAt.toString());

        const tokenToDecode = tokens.idToken || tokens.accessToken;
        const tokenPayload = JSON.parse(atob(tokenToDecode.split('.')[1]));

        // Check for mongoUserId extension first (format: extension_<appId>_mongoUserId)
        const extensionKey = Object.keys(tokenPayload).find(key => key.includes('mongoUserId'));
        const mongoUserId = extensionKey ? tokenPayload[extensionKey] : null;

        // Use mongoUserId if available, otherwise use B2C object ID
        const userId = mongoUserId || tokenPayload.oid || tokenPayload.sub;
        const userEmail = tokenPayload.emails?.[0] || email;

        // Check if the user is disabled in Mongo before proceeding
        const customData = await getCustomData(userId);
        if (customData?.disabled) {
          // Clear tokens if user is disabled
          localStorage.removeItem('b2c_access_token');
          localStorage.removeItem('b2c_refresh_token');
          localStorage.removeItem('b2c_token_expires_at');
          throw new Error('Your account has been disabled. Please contact support.');
        }
        const otherUsers = await getOtherUsers(customData?.group_id, userId);

        setAuthState({
          ...authState,
          isLoggedIn: true,
          currentUser: { email: userEmail, id: userId },
          userCustomData: customData,
          otherUsers: otherUsers,
        });

        return true;
      } catch (error) {
        console.error('B2C login failed:', error);
        throw error;
      }
    };

    const handleRefreshCustomData = async () => {
      const customData = await getCustomData(authState.currentUser.id);
      if (customData) {
        const otherUsers = await getOtherUsers(customData.group_id);

        setAuthState(prevState => ({
          ...prevState,
          userCustomData: customData,
          clientName: customData.clientName || 'orma',
          otherUsers: otherUsers,
        }));
      }
    };

    const updateCustomData = async (customData) => {
      try {
        // Find the user's MongoDB _id
        const queryResult = await findUsersByQuery({ id: authState.currentUser.id });
        if (queryResult?.users?.length > 0) {
          const mongoUser = queryResult.users[0];
          await updateUserApi(mongoUser._id, customData);

          setAuthState({
            ...authState,
            userCustomData: {
              ...authState.userCustomData,
              ...customData,
            },
            clientName: customData.clientName
              ? customData.clientName
              : 'orma',
          });
        }
      } catch (error) {
        console.error('Error updating custom data:', error);
        throw error;
      }
    };

    const updateClinicalRules = async (key, value) => {
      const updates = {
        clinical_rules: {
          ...authState.userCustomData.clinical_rules,
          [key]: value,
        },
      };
      await updateGroupById(authState.userCustomData.group_id, updates);
      setAuthState({
        ...authState,
        userCustomData: {
          ...authState.userCustomData,
          clinical_rules: {
            ...authState.userCustomData.clinical_rules,
            [key]: value,
          },
        },
      });
    };

    const updateBillingConfig = async (billingConfig) => {
      await updateGroupById(authState.userCustomData.group_id, {
        billingConfig: { ...billingConfig },
      });
      setAuthState({
        ...authState,
        userCustomData: {
          ...authState.userCustomData,
          billingConfig: { ...billingConfig },
        },
      });
    };

    const updateShowLiveBilling = async (newValue) => {
      await updateGroupById(authState.userCustomData.group_id, {
        showLiveBilling: newValue,
      });
      setAuthState({
        ...authState,
        userCustomData: {
          ...authState.userCustomData,
          showLiveBilling: newValue,
        },
      });
    };

    const updateSMSTemplates = async (newSMSTemplates) => {
      await updateGroupById(authState.userCustomData.group_id, {
        smsTemplates: { ...newSMSTemplates },
      });
      setAuthState({
        ...authState,
        userCustomData: {
          ...authState.userCustomData,
          smsTemplates: { ...newSMSTemplates },
        },
      });
    };

    const updateConsents = async (consents) => {
      await updateGroupById(authState.userCustomData.group_id, consents);
      setAuthState({
        ...authState,
        userCustomData: {
          ...authState.userCustomData,
          ...consents,
        },
      });
    };

    const getOtherUsers = async (groupId, userId = authState.currentUser.id) => {
      try {
        // Get current user
        const currentUserResult = await findUsersByQuery({ id: userId });
        if (!currentUserResult?.users?.length) {
          return [];
        }

        const currentUserDoc = currentUserResult.users[0];

        // only for super user environments (Iris Health, CVMG)
        let users = [];
        if (currentUserDoc.superUserAccess) {
          const workspacesArr = currentUserDoc.workspaces.map(
            (workspace) => workspace.group_id,
          );
          const result = await findUsersByQuery({
            group_id: { $in: workspacesArr },
          });
          users = result?.users || [];
        } else {
          const result = await findUsersByQuery({ group_id: groupId });
          users = result?.users || [];
        }

        // Deduplicate users by id to prevent React key warnings
        const uniqueUsers = [];
        const seenIds = new Set();
        for (const user of users) {
          if (!seenIds.has(user.id)) {
            seenIds.add(user.id);
            uniqueUsers.push(user);
          }
        }

        return uniqueUsers;
      } catch (error) {
        console.error('Error getting other users:', error);
        return [];
      }
    };

    const handleWorkspaceChange = async (groupIdNew) => {
      try {
        // Guard clause to ensure user data is loaded before proceeding
        if (!authState.userCustomData?._id) {
          console.error('Workspace change attempted before user data was loaded.');
          return;
        }

        await updateUserApi(authState.userCustomData._id, {
          group_id: groupIdNew,
        });

        await handleRefreshCustomData();
      } catch (error) {
        console.error('Error changing workspace:', error);
        throw error;
      }
    };

    const handleEmailNotifications = async (value) => {
      try {
        const queryResult = await findUsersByQuery({ id: authState.currentUser.id });
        if (queryResult?.users?.length > 0) {
          const mongoUser = queryResult.users[0];
          await updateUserApi(mongoUser._id, {
            emailNotifications: value,
          });
        }
        setAuthState({
          ...authState,
          userCustomData: {
            ...authState.userCustomData,
            emailNotifications: value,
          },
        });
      } catch (error) {
        console.error('Error updating email notifications:', error);
        throw error;
      }
    };

    const updateExcludePatientTime = async (uid, newVal) => {
      // TODO: this needs a backend function
    };

    const {
      isLoggedIn,
      currentUser,
      userCustomData,
      clientName,
      otherUsers,
      db,
    } = authState;
    const value = {
      isLoggedIn,
      currentUser,
      userCustomData,
      clientName,
      otherUsers,
      db,
      actions: {
        handleLogout,
        handleB2CLogin,
        handleRefreshCustomData,
        updateCustomData,
        updateClinicalRules,
        updateBillingConfig,
        updateShowLiveBilling,
        updateSMSTemplates,
        updateConsents,
        getOtherUsers,
        handleAddUser,
        handleWorkspaceChange,
        handleEmailNotifications,
        updateExcludePatientTime,
        updateUser,
      },
    };
    return value;
  }, [authState]);
  return (
    <StitchAuthContext.Provider value={authInfo}>
      {props.children}
    </StitchAuthContext.Provider>
  );
}
StitchAuthProvider.propTypes = {
  children: PropTypes.element,
};
